import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageCircle, X, Send, Bot, User, Loader2, Home, Heart, Briefcase, Gift, ShoppingBag, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MACRO_AREAS_IT = [
  {
    id: 'casa', icon: Home, label: 'Casa', color: 'bg-blue-100 text-blue-700',
    topics: [
      { label: 'Bonus ristrutturazione', query: 'Quali sono le detrazioni fiscali per la ristrutturazione della casa?' },
      { label: 'Superbonus / Ecobonus', query: 'Come funziona il Superbonus e l\'Ecobonus per la casa?' },
      { label: 'Bonus mobili', query: 'Come funziona il bonus mobili e quali sono i requisiti?' },
      { label: 'Mutuo prima casa', query: 'Quali sono le detrazioni fiscali per il mutuo prima casa?' },
      { label: 'Affitto e locazioni', query: 'Quali sono le detrazioni per l\'affitto e come funziona la cedolare secca?' },
    ]
  },
  {
    id: 'salute', icon: Heart, label: 'Salute', color: 'bg-rose-100 text-rose-700',
    topics: [
      { label: 'Spese mediche', query: 'Quali spese mediche posso detrarre nella dichiarazione dei redditi?' },
      { label: 'Spese dentistiche', query: 'Le spese dentistiche sono detraibili? Come funziona?' },
      { label: 'Farmaci e dispositivi', query: 'Come detrarre le spese per farmaci e dispositivi medici?' },
      { label: 'Assicurazione sanitaria', query: 'Le spese per assicurazioni sanitarie sono deducibili?' },
    ]
  },
  {
    id: 'altre-spese', icon: ShoppingBag, label: 'Altre Spese', color: 'bg-violet-100 text-violet-700',
    topics: [
      { label: 'Spese funebri', query: 'Le spese funebri sono detraibili fiscalmente?' },
      { label: 'Spese veterinarie', query: 'Come detrarre le spese veterinarie?' },
      { label: 'Donazioni e erogazioni', query: 'Le donazioni sono detraibili o deducibili? Quali sono i limiti?' },
      { label: 'Assicurazioni', query: 'Quali premi assicurativi sono detraibili nella dichiarazione dei redditi?' },
    ]
  },
  {
    id: 'agevolazioni', icon: Gift, label: 'Agevolazioni', color: 'bg-emerald-100 text-emerald-700',
    topics: [
      { label: 'Credito d\'imposta', query: 'Quali crediti d\'imposta sono disponibili per le imprese?' },
      { label: 'Figli a carico', query: 'Come funzionano le detrazioni per figli a carico?' },
      { label: 'Bonus lavoratori', query: 'Quali bonus e agevolazioni esistono per i lavoratori dipendenti?' },
      { label: 'Spese istruzione', query: 'Le spese di istruzione e universitarie sono detraibili?' },
    ]
  },
  {
    id: 'imprese', icon: Briefcase, label: 'Imprese', color: 'bg-amber-100 text-amber-700',
    topics: [
      { label: 'Regime forfettario', query: 'Quali sono i requisiti e i vantaggi del regime forfettario?' },
      { label: 'Apertura P.IVA', query: 'Come si apre una Partita IVA e quali sono i costi?' },
      { label: 'Startup innovativa', query: 'Quali agevolazioni fiscali esistono per le startup innovative?' },
      { label: 'E-commerce', query: 'Quali sono gli adempimenti fiscali per un e-commerce?' },
      { label: 'Contributi INPS', query: 'Come funzionano i contributi INPS per artigiani e commercianti?' },
    ]
  }
];

const MACRO_AREAS_EN = [
  {
    id: 'casa', icon: Home, label: 'Home & Property', color: 'bg-blue-100 text-blue-700',
    topics: [
      { label: 'Renovation bonus', query: 'What are the tax deductions for home renovation in Italy?' },
      { label: 'Superbonus / Ecobonus', query: 'How does the Superbonus and Ecobonus work for homes?' },
      { label: 'Furniture bonus', query: 'How does the furniture bonus work and what are the requirements?' },
      { label: 'First home mortgage', query: 'What are the tax deductions for first home mortgages?' },
      { label: 'Rent and leasing', query: 'What are the deductions for rent and how does the flat-rate tax work?' },
    ]
  },
  {
    id: 'salute', icon: Heart, label: 'Healthcare', color: 'bg-rose-100 text-rose-700',
    topics: [
      { label: 'Medical expenses', query: 'What medical expenses can I deduct in my Italian tax return?' },
      { label: 'Dental expenses', query: 'Are dental expenses deductible? How does it work?' },
      { label: 'Medicines & devices', query: 'How to deduct expenses for medicines and medical devices?' },
      { label: 'Health insurance', query: 'Are health insurance premiums deductible?' },
    ]
  },
  {
    id: 'altre-spese', icon: ShoppingBag, label: 'Other Expenses', color: 'bg-violet-100 text-violet-700',
    topics: [
      { label: 'Funeral expenses', query: 'Are funeral expenses tax deductible in Italy?' },
      { label: 'Veterinary expenses', query: 'How to deduct veterinary expenses in Italy?' },
      { label: 'Donations', query: 'Are donations deductible or deducible in Italy? What are the limits?' },
      { label: 'Insurance', query: 'Which insurance premiums are deductible in the Italian tax return?' },
    ]
  },
  {
    id: 'agevolazioni', icon: Gift, label: 'Tax Benefits', color: 'bg-emerald-100 text-emerald-700',
    topics: [
      { label: 'Tax credits', query: 'What tax credits are available for businesses in Italy?' },
      { label: 'Child deductions', query: 'How do deductions for dependent children work in Italy?' },
      { label: 'Worker bonuses', query: 'What bonuses and benefits exist for employees in Italy?' },
      { label: 'Education expenses', query: 'Are education and university expenses deductible in Italy?' },
    ]
  },
  {
    id: 'imprese', icon: Briefcase, label: 'Business', color: 'bg-amber-100 text-amber-700',
    topics: [
      { label: 'Flat-rate regime', query: 'What are the requirements and advantages of the Italian flat-rate regime?' },
      { label: 'Opening VAT number', query: 'How to open a VAT number in Italy and what are the costs?' },
      { label: 'Innovative startup', query: 'What tax incentives exist for innovative startups in Italy?' },
      { label: 'E-commerce', query: 'What are the tax obligations for an e-commerce business in Italy?' },
      { label: 'INPS contributions', query: 'How do INPS social security contributions work for Italian businesses?' },
    ]
  }
];

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const { t, i18n } = useTranslation();
  const MACRO_AREAS = i18n.language === 'en' ? MACRO_AREAS_EN : MACRO_AREAS_IT;

  // Auto-open after 5 seconds (only once per session)
  useEffect(() => {
    if (hasAutoOpened) return;
    const timer = setTimeout(() => {
      setIsOpen(true);
      setHasAutoOpened(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, [hasAutoOpened]);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      text: t('chat.welcome')
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [activeArea, setActiveArea] = useState(null);
  const [showAreas, setShowAreas] = useState(true);
  const messagesEndRef = useRef(null);
  const lastBotMsgRef = useRef(null);

  useEffect(() => {
    const lastMsg = messages[messages.length - 1];
    if (lastMsg?.type === 'bot' && lastBotMsgRef.current) {
      lastBotMsgRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, activeArea]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;
    setInput('');
    setShowAreas(false);
    setActiveArea(null);

    setMessages(prev => [...prev, { id: Date.now(), type: 'user', text }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: text,
        session_id: sessionId,
        language: i18n.language
      });
      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: 'bot',
        text: response.data.response
      }]);
    } catch (error) {
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: 'bot',
        text: t('chat.error')
      }]);
    } finally {
      setLoading(false);
      setShowAreas(true);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input.trim());
  };

  const handleTopicClick = (topic) => {
    sendMessage(topic.query);
  };

  const handleAreaClick = (area) => {
    setActiveArea(activeArea?.id === area.id ? null : area);
  };

  return (
    <div className="chat-widget z-[55]" data-testid="chat-widget">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="chat-window"
            data-testid="chat-window"
          >
            {/* Header */}
            <div className="bg-indigo-600 px-4 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm">{t('chat.title')}</h3>
                  <p className="text-indigo-200 text-xs">Studio Professionale Azzano Cantarutti SRL</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-white/80 hover:text-white transition-colors"
                data-testid="close-chat-button"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <ScrollArea className="h-80 p-4">
              <div className="space-y-4">
                {messages.map((msg, idx) => (
                  <div
                    key={msg.id}
                    ref={msg.type === 'bot' && idx === messages.length - 1 ? lastBotMsgRef : null}
                    className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`flex items-start gap-2 max-w-[85%] ${msg.type === 'user' ? 'flex-row-reverse' : ''}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        msg.type === 'user' ? 'bg-indigo-100' : 'bg-slate-100'
                      }`}>
                        {msg.type === 'user' ? (
                          <User className="w-4 h-4 text-indigo-600" />
                        ) : (
                          <Bot className="w-4 h-4 text-slate-600" />
                        )}
                      </div>
                      <div className={`px-4 py-3 text-sm whitespace-pre-line ${
                        msg.type === 'user' ? 'chat-bubble-user' : 'chat-bubble-bot'
                      }`}>
                        {msg.text}
                      </div>
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="flex items-start gap-2">
                      <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                        <Bot className="w-4 h-4 text-slate-600" />
                      </div>
                      <div className="chat-bubble-bot px-4 py-3">
                        <Loader2 className="w-4 h-4 animate-spin" />
                      </div>
                    </div>
                  </div>
                )}

                {/* Macro Areas */}
                {showAreas && !loading && (
                  <div className="space-y-2" data-testid="chat-macro-areas">
                    {!activeArea ? (
                      <div className="flex flex-wrap gap-1.5">
                        {MACRO_AREAS.map((area) => (
                          <button
                            key={area.id}
                            onClick={() => handleAreaClick(area)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all hover:scale-105 ${area.color}`}
                            data-testid={`chat-area-${area.id}`}
                          >
                            <area.icon className="w-3.5 h-3.5" />
                            {area.label}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <motion.div
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-1.5"
                      >
                        <button
                          onClick={() => setActiveArea(null)}
                          className="flex items-center gap-1 text-xs text-indigo-600 font-medium hover:underline mb-1"
                          data-testid="chat-back-areas"
                        >
                          <ArrowLeft className="w-3 h-3" />
                          Tutte le aree
                        </button>
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{activeArea.label}</p>
                        {activeArea.topics.map((topic, i) => (
                          <button
                            key={i}
                            onClick={() => handleTopicClick(topic)}
                            className="block w-full text-left px-3 py-2 text-xs bg-slate-50 hover:bg-indigo-50 hover:text-indigo-700 rounded-lg transition-colors border border-slate-100"
                            data-testid={`chat-topic-${activeArea.id}-${i}`}
                          >
                            {topic.label}
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Input */}
            <form onSubmit={handleSubmit} className="p-4 border-t border-slate-200 dark:border-slate-700">
              <div className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={t('chat.placeholder')}
                  className="flex-1"
                  disabled={loading}
                  data-testid="chat-input"
                />
                <Button 
                  type="submit" 
                  size="icon" 
                  disabled={loading || !input.trim()}
                  className="btn-accent"
                  data-testid="send-message-button"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-2 text-center">
                {i18n.language === 'en' ? 'Answers are informational. Always consult a professional.' : 'Le risposte sono informative. Consulta sempre un professionista.'}
              </p>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toggle Button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-colors relative z-[60] ${
          isOpen ? 'bg-slate-900' : 'bg-indigo-600 hover:bg-indigo-700'
        }`}
        data-testid="toggle-chat-button"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}
      </motion.button>
    </div>
  );
};

export default ChatWidget;
